import { getApiConfig } from './api-config';
import { logApiRequest } from './events';
import { SIMILARITY_PROMPT } from './prompts';

export class GroqAPI {
  static async compareSimilarities(
    sourceQuestion: string,
    questionsToCompare: string[]
  ): Promise<number[]> {
    const config = getApiConfig();
    
    if (!config.apiKeys.groq) {
      throw new Error('Groq API key is missing');
    }

    try {
      const batchSize = SIMILARITY_PROMPT.calculateBatchSize(sourceQuestion, questionsToCompare);
      const batches = [];

      for (let i = 0; i < questionsToCompare.length; i += batchSize) {
        const batchQuestions = questionsToCompare.slice(i, i + batchSize);
        const request = {
          model: 'mixtral-8x7b-32768',
          messages: [
            { role: "system", content: SIMILARITY_PROMPT.system },
            { 
              role: "user", 
              content: SIMILARITY_PROMPT.generateUserPrompt(sourceQuestion, batchQuestions)
            }
          ],
          temperature: 0.1,
          max_tokens: 1000
        };

        logApiRequest({
          provider: 'groq',
          request: {
            ...request,
            apiKey: '[REDACTED]',
            batchSize,
            batchNumber: Math.floor(i / batchSize) + 1,
            totalBatches: Math.ceil(questionsToCompare.length / batchSize)
          }
        });

        const response = await fetch('https://api.groq.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${config.apiKeys.groq}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(request)
        });

        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const content = data.choices[0]?.message?.content;

        if (!content) {
          throw new Error('Empty response from Groq');
        }

        try {
          const scores = SIMILARITY_PROMPT.parseScores(content, batchQuestions.length);
          batches.push(scores);

          logApiRequest({
            provider: 'groq',
            request: {
              ...request,
              apiKey: '[REDACTED]'
            },
            response: { scores }
          });
        } catch (parseError) {
          console.error('Failed to parse response:', parseError, '\nContent:', content);
          throw parseError;
        }
      }

      return batches.flat();
    } catch (error) {
      logApiRequest({
        provider: 'groq',
        request: {
          sourceQuestion,
          questionsCount: questionsToCompare.length
        },
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error getting semantic similarity from Groq:', errorMessage);
      throw new Error(`Failed to compare questions: ${errorMessage}`);
    }
  }
}